import {
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
} from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { type ReactElement, type ReactNode, useState } from "react";
import { Link } from "react-router-dom";

export type ConfigSidebarItem = {
  path: string;
  label: string;
  icon: ReactElement;
};

export type ConfigSidebarSection = {
  name: string;
  icon: ReactElement;
  items: ConfigSidebarItem[];
};

export type ConfigSidebarGroup = ConfigSidebarSection & {
  content?: ReactNode;
};

export const CONFIGURATION_BASE_PATH = "/configuration";
export const SIDEBAR_EXPANDED_WIDTH = 240;
export const SIDEBAR_COLLAPSED_WIDTH = 56;

const isItemSelected = (currentPath: string, itemPath: string) =>
  currentPath === itemPath || currentPath.startsWith(`${itemPath}/`);

const GroupHeader = ({ name }: { name: string }) => (
  <Typography
    component="div"
    sx={{
      px: 2.5,
      pt: 1,
      pb: 0.5,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "text.secondary",
    }}
  >
    {name}
  </Typography>
);

type SidebarLinkProps = {
  item: ConfigSidebarItem;
  selected: boolean;
  onClick?: () => void;
};

const SidebarLink = ({ item, selected, onClick }: SidebarLinkProps) => (
  <ListItemButton
    component={Link}
    to={`${CONFIGURATION_BASE_PATH}/${item.path}`}
    selected={selected}
    onClick={onClick}
    sx={(theme) => ({
      mx: 1,
      my: 0.25,
      py: 0.5,
      px: 1.5,
      borderRadius: 1,
      color: theme.palette.text.primary,
      "& .MuiSvgIcon-root": {
        fontSize: 20,
        color: theme.palette.text.secondary,
      },
      "& .MuiListItemText-primary": { fontSize: 14 },
      "&.Mui-selected": {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        color: theme.palette.primary.main,
        "& .MuiSvgIcon-root": { color: theme.palette.primary.main },
        "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
      },
    })}
  >
    <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
    <ListItemText primary={item.label} />
  </ListItemButton>
);

type Props = {
  groups: ConfigSidebarGroup[];
  currentPath: string;
  collapsed: boolean;
  onToggle?: () => void;
  onNavigate?: () => void;
};

export const ConfigurationSidebar = ({
  groups,
  currentPath,
  collapsed,
  onToggle,
  onNavigate,
}: Props) => {
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const handleNavigate = () => {
    setOpenGroup(null);
    onNavigate?.();
  };

  const renderExpandedGroup = (group: ConfigSidebarGroup) => (
    <Box key={group.name} sx={{ mb: 1 }}>
      {group.content ? (
        <Box onClick={handleNavigate}>{group.content}</Box>
      ) : (
        <>
          <GroupHeader name={group.name} />
          {group.items.map((item) => (
            <SidebarLink
              key={item.path}
              item={item}
              selected={isItemSelected(currentPath, item.path)}
              onClick={handleNavigate}
            />
          ))}
        </>
      )}
    </Box>
  );

  const renderCollapsedGroup = (group: ConfigSidebarGroup) => {
    const groupSelected = group.items.some((item) =>
      isItemSelected(currentPath, item.path),
    );
    const singleItem =
      !group.content && group.items.length === 1 ? group.items[0] : null;

    const flyout = singleItem ? (
      singleItem.label
    ) : (
      <Box sx={{ py: 1, minWidth: 220 }}>
        {group.content ? (
          <Box onClick={handleNavigate}>{group.content}</Box>
        ) : (
          <>
            <GroupHeader name={group.name} />
            {group.items.map((item) => (
              <SidebarLink
                key={item.path}
                item={item}
                selected={isItemSelected(currentPath, item.path)}
                onClick={handleNavigate}
              />
            ))}
          </>
        )}
      </Box>
    );

    const firstItem = group.items[0];

    return (
      <Tooltip
        key={group.name}
        title={flyout}
        placement={singleItem ? "right" : "right-start"}
        open={openGroup === group.name}
        onOpen={() => setOpenGroup(group.name)}
        onClose={() => setOpenGroup(null)}
        slotProps={
          singleItem
            ? undefined
            : {
                tooltip: {
                  sx: (theme) => ({
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: theme.shadows[4],
                    borderRadius: 1,
                    p: 0,
                    maxWidth: "none",
                    maxHeight: "70vh",
                    overflowY: "auto" as const,
                  }),
                },
              }
        }
      >
        {firstItem ? (
          <ListItemButton
            component={Link}
            to={`${CONFIGURATION_BASE_PATH}/${firstItem.path}`}
            selected={groupSelected}
            onClick={handleNavigate}
            aria-label={group.name}
            sx={(theme) => ({
              mx: 1,
              my: 0.25,
              height: 40,
              justifyContent: "center",
              borderRadius: 1,
              "& .MuiSvgIcon-root": {
                fontSize: 20,
                color: theme.palette.text.secondary,
              },
              "&.Mui-selected": {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                "& .MuiSvgIcon-root": { color: theme.palette.primary.main },
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.16),
                },
              },
            })}
          >
            {group.icon}
          </ListItemButton>
        ) : (
          <ListItemButton
            aria-label={group.name}
            sx={(theme) => ({
              mx: 1,
              my: 0.25,
              height: 40,
              justifyContent: "center",
              borderRadius: 1,
              "& .MuiSvgIcon-root": {
                fontSize: 20,
                color: theme.palette.text.secondary,
              },
              "&.Mui-selected": {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                "& .MuiSvgIcon-root": { color: theme.palette.primary.main },
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.16),
                },
              },
            })}
          >
            {group.icon}
          </ListItemButton>
        )}
      </Tooltip>
    );
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minHeight: 52,
          pl: 1.5,
          pr: 1,
        }}
      >
        {onToggle && (
          <Tooltip
            title={collapsed ? "Expand menu" : "Collapse menu"}
            placement="right"
          >
            <IconButton size="small" onClick={onToggle}>
              {collapsed ? (
                <MenuIcon fontSize="small" />
              ) : (
                <MenuOpenIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
        {!collapsed && (
          <Typography
            variant="subtitle1"
            color="primary"
            noWrap
            sx={{ fontWeight: 600, pl: onToggle ? 0 : 1 }}
          >
            Configuration
          </Typography>
        )}
      </Box>
      <Divider />
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", py: 1 }}>
        {groups.map((group) =>
          collapsed ? renderCollapsedGroup(group) : renderExpandedGroup(group),
        )}
      </Box>
    </>
  );
};
